import React, { FC } from 'react';
import { FaEdit } from 'react-icons/fa';
import { CellProps, Column } from 'react-table';
import { useHistory, useParams } from 'react-router';
import { useActiveClientContext } from 'store/active-client';
import { buildClientUrl, buildClientEntity } from 'utils/helpers/client';
import FetchTable from 'components/table/FetchTable';
import Button from 'components/Button';
import Badge from 'components/Badge';
import TooltipWrapper from 'components/TooltipWrapper';
import DeleteEntity from 'components/crud/DeleteEntity';
import { IElement } from '../types';

interface IProps {
  entityName: string;
  entityEndpoint: string;
  segmentFilter: string;
}

const List: FC<IProps> = ({ entityName, entityEndpoint, segmentFilter }) => {
  const { id: segmentId } = useParams<{
    id: string | undefined;
  }>();
  const { client } = useActiveClientContext();
  const history = useHistory();
  const createElement = () => history.push(buildClientUrl(`/segments/${segmentId}/elements/create`, client));

  const columns: Array<Column<IElement>> = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: 'Име',
      accessor: 'name',
      width: '50%',
    },
    {
      Header: 'Съдържание',
      accessor: 'contents',
      width: '50%',
      Cell: ({ row }: CellProps<IElement>) => {
        const { contents } = row.original;

        /**
         * Strip HTML tags and limit content preview to 20 words
         */
        const contentWords = contents.replace(/(<([^>]+)>)/gi, '').split(' ');
        if (contentWords.length > 20) {
          return <div>{contentWords.slice(0, 20).join(' ')} [...]</div>;
        }

        return <div>{contents.replace(/(<([^>]+)>)/gi, '')}</div>;
      },
    },
    {
      Header: 'Статус',
      accessor: 'status',
      width: '100%',
      minWidth: 100,
      disableSortBy: true,
      Cell: ({ row }: CellProps<IElement>) => {
        const isActive = row.original.is_active;

        if (isActive)
          return (
            <Badge
              label="Активен"
              variant="primary"
            />
          );

        return (
          <Badge
            label="Неактивен"
            variant="danger"
          />
        );
      },
    },
    {
      Header: () => <div className="text-right">Действия</div>,
      accessor: 'actions',
      disableSortBy: true,
      maxWidth: 150,
      width: 150,
      minWidth: 150,
      Cell: ({ cell }: CellProps<IElement>) => {
        const { id, name } = cell.row.original;
        return (
          <div className="text-right">
            <div className="d-inline-block mr-2">
              <TooltipWrapper
                id="tooltip-edit"
                overlay="Редактирай"
              >
                <Button
                  btnType="primary"
                  size="sm"
                  outlined
                  onClick={() => history.push(buildClientUrl(`/segments/${segmentId}/elements/edit/${id}`, client))}
                >
                  <FaEdit size={14} />
                </Button>
              </TooltipWrapper>
            </div>
            <DeleteEntity
              id={id}
              entity={entityEndpoint}
              successMsg="Елементът беше изтрит."
              confirmHeading="Изтриване на елемент"
              confirmMsg={
                <span>
                  Сигурни ли сте, че желаете да изтриете елемент&nbsp;
                  <span className="font-weight-bold">{name}</span>?
                </span>
              }
              queryKey={buildClientEntity(entityName, client)}
            />
          </div>
        );
      },
    },
  ];

  return (
    <FetchTable<IElement>
      entityName={buildClientEntity(entityName, client)}
      entityEndpoint={entityEndpoint}
      searchParams={{ [segmentFilter]: segmentId }}
      columns={columns}
      onAdd={createElement}
      addBtnLabel="Добавяне на елемент"
      pageLimit={10}
      pagination
    />
  );
};

export default List;
